import type { NextStep } from '@repo/common';

import { ActionType, Step } from '@repo/common';
import { useEffect } from 'react';

import { setWorkflowAction } from '@/providers/app-provider/actions';
import { useAppContext } from '@/providers/app-provider/context';
import { postSubmitHandled } from '@/services/rivaas-api';
import { mapBffErrorCodeToFailReason } from '@/utils/bff-error-mapping';
import { applyBffSubmitResponse } from '@/utils/submit-error';
import { nextWorkflowFromSubmit } from '@/utils/step-workflow';
import { resolveVerificationRedirect } from '@/utils/workflow';

export function useHandleAction(nextStep: NextStep | null) {
  const {
    dispatch,
    state: { customerData, viewType },
  } = useAppContext();

  useEffect(() => {
    if (!nextStep?.action) {
      return;
    }

    if (nextStep.action.type === ActionType.OPTIONS) {
      return;
    }

    async function evaluate() {
      const redirectOptions = {
        unverifiedUrl: customerData?.redirect?.unverifiedUrl,
        verifiedUrl: customerData?.redirect?.verifiedUrl,
      };
      const data = await postSubmitHandled();

      if (applyBffSubmitResponse(dispatch, data, redirectOptions)) {
        return;
      }

      const workflowBase = nextWorkflowFromSubmit(data, redirectOptions);

      if (workflowBase?.nextStep.value === Step.FAIL) {
        const failReason = mapBffErrorCodeToFailReason(data.error?.code);

        dispatch(
          setWorkflowAction({
            ...workflowBase,
            nextStep: {
              ...workflowBase.nextStep,
              failReason,
            },
          }),
        );
      }
    }

    const { action } = nextStep;

    switch (action.type) {
      case ActionType.REDIRECT:
        const { payload, redirectTimeout, url } = action.data;

        if (redirectTimeout !== undefined) {
          return;
        }

        resolveVerificationRedirect({ url, viewType: viewType!, payload });

        break;

      case ActionType.EVALUATE:
        evaluate();

        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextStep]);
}
